/**
 * @file projects-filter.ts
 * @description Pure helper that filters a list of projects by free-text
 *   query AND status. Shared by the customer dashboard (their own
 *   projects, no customer label needed) and the admin all-projects
 *   page (where the search also matches the joined customer name /
 *   email via the `getCustomerLabel` callback).
 * @module Lib
 * @security Filter is local-only; nothing here echoes to the server.
 *   The customer label callback receives only data already returned
 *   for the calling user, so cross-tenant leakage isn't possible.
 */

import { daysUntil } from './date-helpers';
import type { ProjectDto, ProjectPriority, ProjectStatus } from './projects-api';

/** Slice 73 — deadline buckets driven by desired_deadline.
 *   - overdue: deadline strictly before today
 *   - this_week: deadline in [today, today + 7)
 *   - later: deadline ≥ 7 days out
 *   - no_deadline: desired_deadline is null
 *   - all: no filter
 *
 *  Terminal-status projects (completed/cancelled) are excluded from
 *  every non-"all" bucket — their deadline is moot and admins
 *  filtering for "overdue" don't want to see finished work.
 */
export type DeadlineFilter = 'all' | 'overdue' | 'this_week' | 'later' | 'no_deadline';

export interface ProjectFilters {
  query: string;
  status: ProjectStatus | 'all';
  /** Slice 62 — admin can narrow to 'urgent' or 'high' only. 'all'
   *  keeps the existing behaviour (any priority, including null). */
  priority: ProjectPriority | 'all';
  /** Slice 73 — deadline bucket. See DeadlineFilter for semantics. */
  deadline: DeadlineFilter;
}

export const EMPTY_PROJECT_FILTERS: ProjectFilters = {
  query: '',
  status: 'all',
  priority: 'all',
  deadline: 'all',
};

const TERMINAL_STATUSES: ReadonlySet<ProjectStatus> = new Set(['completed', 'cancelled']);

/** Pure — exported for tests + the chip bar's per-bucket counts.
 *  Terminal-status projects always bucket as 'all' (i.e. they only
 *  show when the deadline filter is 'all'). */
export function deadlineBucket(p: ProjectDto, now?: Date): DeadlineFilter {
  if (TERMINAL_STATUSES.has(p.status)) return 'all';
  if (p.desired_deadline === null) return 'no_deadline';
  const days = daysUntil(p.desired_deadline, now);
  if (days === null) return 'all';
  if (days < 0) return 'overdue';
  if (days < 7) return 'this_week';
  return 'later';
}

/**
 * Returns the SAME array (identity, not a copy) when all filters are
 * at defaults so the common case is allocation-free. Query is matched
 * case-insensitively against title + description + (optionally)
 * a per-row customer label resolved by the caller.
 */
export function applyProjectFilters(
  projects: ProjectDto[],
  f: ProjectFilters,
  getCustomerLabel?: (p: ProjectDto) => string | undefined,
): ProjectDto[] {
  const q = f.query.trim().toLowerCase();
  const statusOn = f.status !== 'all';
  const priorityOn = f.priority !== 'all';
  const deadlineOn = f.deadline !== 'all';
  if (q === '' && !statusOn && !priorityOn && !deadlineOn) return projects;
  return projects.filter((p) => {
    if (statusOn && p.status !== f.status) return false;
    if (priorityOn && p.priority !== f.priority) return false;
    if (deadlineOn && deadlineBucket(p) !== f.deadline) return false;
    if (q !== '') {
      const customer = (getCustomerLabel?.(p) ?? '').toLowerCase();
      const hay = `${p.title} ${p.description} ${customer}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}
